import { PdfMediaFile } from "@/features/media/types/media.types";
import type { Survey } from "@/features/survey/types";
import dayjs from "dayjs";

export const formatDate = (date: Date | string | number): string => {
  if (!date) return "";
  return dayjs(date).format("MMM. D, YYYY");
};

/** Checks if uploaded media is a PDF file for direct embedding */
export const isPdfFile = (file: PdfMediaFile): boolean =>
  file.mediaType === "application/pdf" ||
  file.displayName?.toLowerCase().endsWith(".pdf") ||
  file.keyName?.toLowerCase().endsWith(".pdf") ||
  false;

/**
 * Flattens media files by replacing original files with their modified versions.
 * - Finds files where item.mediaId === res.modifiedFromMediaId
 * - Replaces the original with the modified version
 * - Removes the duplicate modified entry
 */
export const flattenMediaFilesWithModifiedFiles = <
  T extends { mediaId: string; modifiedFromMediaId?: string },
>(
  mediaFiles: T[],
): T[] => {
  if (!mediaFiles || mediaFiles.length === 0) return [];

  const modifiedFilesMap = new Map<string, T>();
  const regularFiles: T[] = [];

  mediaFiles.forEach((file) => {
    if (file.modifiedFromMediaId) {
      modifiedFilesMap.set(file.modifiedFromMediaId, file);
    } else {
      regularFiles.push(file);
    }
  });

  return regularFiles.map((file) => modifiedFilesMap.get(file.mediaId) ?? file);
};

export const hasAddressLine2AsCustom = (currentSurvey: Survey) => {
  return currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.some(
    (rule) => rule.fieldLabel === "Address Line 2" && !rule.fieldKey,
  );
};

export {
  getPropertyDisplayNumber,
  getPropertyMarkerContent,
} from "./propertyUtils";
